import { forwardRef } from 'react';
import clsx from 'clsx';
import { overrideTailwindClasses } from 'tailwind-override';

import { ButtonModes } from 'constants/ButtonModes';

import Button, { ButtonProps } from './Button';
import Icon, { IconType } from './Icon';

type IconButtonProps = {
  isDark?: boolean;
  hasBorder?: boolean;
  icon: IconType;
  iconClassName?: string;
  width?: number;
  height?: number;
} & Omit<ButtonProps, 'aria-label'> &
  Required<{ 'aria-label': string }>;

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      fill = 'currentColor',
      hasBorder = true,
      icon,
      iconClassName,
      isDark,
      isDisabled,
      mode = isDark ? ButtonModes.flatBlack : ButtonModes.flat,
      size = 'full',
      ...rest
    },
    ref
  ) => {
    const classes = overrideTailwindClasses(
      clsx(
        'iconBtn rounded-full flex items-center justify-center max-h-10',
        mode,
        {
          [className || '']: className,
          'w-10 h-10 px-0': size === 'full',
          'w-8 h-8': size === 'small',
          dark: isDark,
          hasBorder,
        }
      )
    );

    return (
      <Button
        ref={ref}
        className={classes}
        mode={ButtonModes.none}
        isDisabled={isDisabled}
        {...rest}
      >
        <Icon
          className={iconClassName}
          size={rest.iconSize}
          fill={fill}
          icon={icon}
        />
      </Button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
